import { useState } from 'react';
import API from '@/lib/api';
import { Package, FileXls, DownloadSimple, Printer, X as CloseIcon, PencilSimple, Check, FloppyDisk, ArrowCounterClockwise } from '@phosphor-icons/react';

const SECTORS = [
  { v: 'construction', l: '🏗️ Bouw (bundels)' },
  { v: 'clothing', l: '👕 Kleding (dozen)' },
  { v: 'food', l: '🍎 Voeding (dozen)' },
  { v: 'general', l: '📦 Algemeen' },
];
const num = (v) => { const f = parseFloat(String(v ?? '').replace(',', '.')); return isNaN(f) ? 0 : f; };
const r2 = (v) => Math.round(v * 100) / 100;

function NumCell({ value, onChange, testId, step = '0.01', className = '' }) {
  return (
    <input
      type="number"
      step={step}
      className={`field-input text-xs text-right font-mono py-1 px-2 ${className}`}
      style={{ width: 96 }}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      data-testid={testId}
    />
  );
}

export default function PackingListModal({ docId, initial, onClose }) {
  const [preview, setPreview] = useState(initial.preview);
  const [sector, setSector] = useState(initial.sector);
  const [edit, setEdit] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const s = preview.samenvatting;

  const reload = async (sec) => {
    setBusy(true);
    try {
      const r = await API.post('/packing-from-invoice', { doc_id: docId, sector: sec });
      setPreview(r.data); setSector(sec); setDirty(false);
    } finally { setBusy(false); }
  };

  const updLijn = (i, key, val) => {
    setPreview(p => {
      const lijnen = p.lijnen.map((l, idx) => idx === i ? { ...l, [key]: val } : l);
      const sum = (k) => r2(lijnen.reduce((a, l) => a + num(l[k]), 0));
      return { ...p, lijnen, samenvatting: { ...p.samenvatting, totaal_units: sum('units'), totaal_pallets: sum('pallets'), totaal_netto_kg: sum('netto_kg'), totaal_bruto_kg: sum('bruto_kg') } };
    });
    setDirty(true);
  };
  const updTotaal = (key, val) => { setPreview(p => ({ ...p, samenvatting: { ...p.samenvatting, [key]: val } })); setDirty(true); };

  const save = async () => {
    setBusy(true);
    try {
      await API.put(`/packing/${docId}/override`, {
        sector,
        lijnen: preview.lijnen.map(l => ({ product: l.product, units: num(l.units), stuks_per_unit: l.stuks_per_unit, pallets: num(l.pallets), netto_kg: num(l.netto_kg), bruto_kg: num(l.bruto_kg) })),
        samenvatting: { totaal_units: num(s.totaal_units), totaal_pallets: num(s.totaal_pallets), totaal_netto_kg: num(s.totaal_netto_kg), totaal_bruto_kg: num(s.totaal_bruto_kg) },
      });
      setEdit(false);
      await reload(sector);
    } catch (e) {
      alert('Opslaan mislukt: ' + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm('Handmatige aanpassingen wissen en terug naar de automatische berekening?')) return;
    setBusy(true);
    try { await API.delete(`/packing/${docId}/override`); setEdit(false); await reload(sector); }
    finally { setBusy(false); }
  };
  const fetchFile = async (kind) => {
    if (dirty) await save();
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/packing/${docId}/${kind}?sector=${sector}`, { credentials: 'include' });
    return URL.createObjectURL(await r.blob());
  };
  const download = async (kind, ext) => {
    const url = await fetchFile(kind);
    const a = document.createElement('a'); a.href = url; a.download = `PackingList-${docId}.${ext}`; a.click();
    URL.revokeObjectURL(url);
  };
  const print = async () => {
    const url = await fetchFile('pdf');
    const w = window.open(url, '_blank');
    if (w) setTimeout(() => { try { w.print(); } catch { /* popup */ } }, 1000);
  };

  const tiles = [
    { key: 'totaal_units', lbl: `Totaal ${s.unit_label}`, val: s.totaal_units, color: 'text-[#002FA7]', step: '1' },
    { key: 'totaal_pallets', lbl: 'Totaal pallets', val: s.totaal_pallets, color: 'text-[#D97706]', step: '1' },
    { key: 'totaal_netto_kg', lbl: 'Netto (kg)', val: num(s.totaal_netto_kg).toFixed(2), color: 'text-[#008A27]' },
    { key: 'totaal_bruto_kg', lbl: `Bruto (kg) +${s.verpakkings_overhead_pct}%`, val: num(s.totaal_bruto_kg).toFixed(2), color: 'text-[#7C3AED]' },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={onClose} data-testid="packing-modal">
      <div className="bg-white w-full max-w-5xl max-h-[92vh] shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Package size={20} weight="bold" className="text-[#002FA7]" />
            <span className="font-bold text-gray-900">Packing List</span>
            <span className="font-mono text-xs text-[#002FA7]">{docId}</span>
            <select className="ml-3 text-xs border border-gray-300 px-2 py-1 bg-white" value={sector} disabled={busy}
              onChange={e => reload(e.target.value)} data-testid="packing-sector-select">
              {SECTORS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
            {preview.has_override && !dirty && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300" title={`Handmatig aangepast op ${preview.override_at ? new Date(preview.override_at).toLocaleString('nl-BE') : '-'}`} data-testid="packing-override-badge">
                ✏️ Handmatig aangepast
              </span>
            )}
            {dirty && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200" data-testid="packing-dirty-badge">Niet opgeslagen</span>}
          </div>
          <div className="flex items-center gap-2">
            {!edit ? (
              <button className="btn btn-secondary btn-sm inline-flex items-center gap-1" onClick={() => setEdit(true)} disabled={busy} data-testid="packing-edit" title="Pas pallets, dozen en gewichten handmatig aan (bv. werkelijke lading)">
                <PencilSimple size={14} /> Bewerken
              </button>
            ) : (
              <button className="btn btn-secondary btn-sm inline-flex items-center gap-1" onClick={() => setEdit(false)} disabled={busy} data-testid="packing-edit-done">
                <Check size={14} /> Klaar
              </button>
            )}
            {dirty && (
              <button className="btn btn-sm inline-flex items-center gap-1 text-white" style={{ background: '#059669', borderColor: '#059669' }} onClick={save} disabled={busy} data-testid="packing-save">
                <FloppyDisk size={14} /> Opslaan
              </button>
            )}
            {(preview.has_override || dirty) && (
              <button className="btn btn-secondary btn-sm inline-flex items-center gap-1" onClick={reset} disabled={busy} data-testid="packing-reset" title="Terug naar automatische berekening">
                <ArrowCounterClockwise size={14} /> Herstel berekening
              </button>
            )}
            <button className="btn btn-secondary btn-sm inline-flex items-center gap-1" onClick={() => download('excel', 'xlsx')} disabled={busy} data-testid="packing-excel"><FileXls size={14} /> Excel</button>
            <button className="btn btn-secondary btn-sm inline-flex items-center gap-1" onClick={() => download('pdf', 'pdf')} disabled={busy} data-testid="packing-download"><DownloadSimple size={14} /> PDF</button>
            <button className="btn btn-primary btn-sm inline-flex items-center gap-1" onClick={print} disabled={busy} data-testid="packing-print"><Printer size={14} /> Afdrukken</button>
            <button className="text-gray-400 hover:text-gray-700 ml-1" onClick={onClose} data-testid="packing-close"><CloseIcon size={22} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          <div className="grid grid-cols-4 border-b border-gray-200">
            {tiles.map((c, i) => (
              <div key={c.key} className={`p-4 text-center ${i < 3 ? 'border-r border-gray-100' : ''}`}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{c.lbl}</div>
                {edit ? (
                  <input type="number" step={c.step || '0.01'} className={`field-input text-center text-xl font-bold font-mono mt-1 mx-auto ${c.color}`} style={{ maxWidth: 160 }}
                    value={s[c.key] ?? ''} onChange={e => updTotaal(c.key, e.target.value)} data-testid={`packing-totaal-${c.key}`} />
                ) : (
                  <div className={`text-2xl font-bold mt-1 ${c.color}`} data-testid={`packing-tile-${c.key}`}>{c.val}</div>
                )}
              </div>
            ))}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-600">
              <tr>
                <th className="text-left px-4 py-2.5">Product</th>
                <th className="text-right px-4 py-2.5">Aantal</th>
                <th className="text-right px-4 py-2.5">{s.unit_label} × per unit</th>
                <th className="text-right px-4 py-2.5">Pallets</th>
                <th className="text-right px-4 py-2.5">Netto (kg)</th>
                <th className="text-right px-4 py-2.5">Bruto (kg)</th>
              </tr>
            </thead>
            <tbody>
              {preview.lijnen.map((l, i) => (
                <tr key={i} className="border-t border-gray-100 hover:bg-gray-50/50">
                  <td className="px-4 py-2 font-medium">{l.product}</td>
                  <td className="px-4 py-2 text-right font-mono">{num(l.aantal).toFixed(2)} {l.eenheid}</td>
                  <td className="px-4 py-2 text-right font-mono text-[#002FA7]">
                    {edit ? <span className="inline-flex items-center gap-1"><NumCell value={l.units} step="1" onChange={v => updLijn(i, 'units', v)} testId={`packing-lijn-units-${i}`} /> × {l.stuks_per_unit}</span> : <><strong>{l.units}</strong> × {l.stuks_per_unit}</>}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{edit ? <NumCell value={l.pallets} onChange={v => updLijn(i, 'pallets', v)} testId={`packing-lijn-pallets-${i}`} /> : l.pallets}</td>
                  <td className="px-4 py-2 text-right font-mono">{edit ? <NumCell value={l.netto_kg} onChange={v => updLijn(i, 'netto_kg', v)} testId={`packing-lijn-netto-${i}`} /> : num(l.netto_kg).toFixed(2)}</td>
                  <td className="px-4 py-2 text-right font-mono font-bold">{edit ? <NumCell value={l.bruto_kg} onChange={v => updLijn(i, 'bruto_kg', v)} testId={`packing-lijn-bruto-${i}`} /> : num(l.bruto_kg).toFixed(2)}</td>
                </tr>
              ))}
              {preview.lijnen.length === 0 && <tr><td colSpan="6" className="text-center text-gray-400 py-8">Geen lijnen om in te pakken</td></tr>}
            </tbody>
          </table>
          <div className="px-5 py-3 bg-blue-50/40 border-t border-blue-100 text-[11px] text-gray-700 flex items-start gap-2">
            <span className="text-base">ℹ️</span>
            <span>
              Sector <strong>{preview.sector_label}</strong> actief. Rules uit <em>instellingen → packing</em> (bundel/doos-regel matcht op productnaam).
              Verpakkings-overhead: <strong>+{s.verpakkings_overhead_pct}%</strong> voor bruto-gewicht.
              {edit && <> <strong>Bewerken:</strong> pas per lijn of de totalen aan (bv. werkelijk 11 pallets / 20.400 kg) en klik Opslaan — PDF, Excel en afdruk gebruiken dan deze waarden.</>}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
